// Campaña de recompra (sql/124): quiénes ya compraron y no han vuelto, en qué
// grupo van según cuánto hace de su última compra, y el mensaje de cada una.
// Funciones puras: se prueban sin base de datos.

export const GRUPOS = [
  { clave: 'recientes', etiqueta: 'Compraron hace poco', icono: '🌱', rango: 'menos de 30 días', idea: 'Complementar lo que compró' },
  { clave: 'medio', etiqueta: 'Hace 30 a 60 días', icono: '💕', rango: '30 a 60 días', idea: 'Novedades en su talla' },
  { clave: 'dormidas', etiqueta: 'Más de 60 días', icono: '🎁', rango: 'más de 60 días', idea: 'Le extrañamos + beneficio' },
];

// Mensajes por defecto (de usted: es chat 1 a 1 con la clienta). Se pueden editar.
export const PLANTILLAS = {
  recientes: '¡Hola {nombre}! 😊 Le saluda {tienda}. ¿Qué tal le ha ido con {lo_que_compro}? Le comparto nuestro catálogo con todo lo disponible{talla_txt}:\n{enlace}\n\nPor ser nuestra clienta tiene {descuento}% de descuento en su compra hasta el {hasta} 💕',
  medio: '¡Hola {nombre}! 😊 Le saluda {tienda}. Nos llegaron piezas nuevas{talla_txt} y pensamos en usted 😍 Véalas aquí:\n{enlace}\n\nPor ser clienta tiene {descuento}% de descuento hasta el {hasta} 🎁',
  dormidas: '¡Hola {nombre}! 😊 Le saluda {tienda}. ¡Hace rato no sabemos de usted! 💕 Para que nos visite de nuevo le regalamos {descuento}% de descuento en su compra hasta el {hasta}. Mire lo que tenemos disponible{talla_txt}:\n{enlace}',
};

const DIA = 86400000;
const norm = (t) => String(t || '').trim();

export function grupoPorDias(dias) {
  if (dias < 30) return 'recientes';
  if (dias <= 60) return 'medio';
  return 'dormidas';
}

// Lo más repetido de una lista (categoría o talla favorita).
function moda(lista) {
  const c = new Map();
  lista.filter(Boolean).forEach((x) => c.set(x, (c.get(x) || 0) + 1));
  let mejor = null; let n = 0;
  c.forEach((v, k) => { if (v > n) { mejor = k; n = v; } });
  return mejor;
}

const ticketDe = (v) => String(v.ticket_id || `${v.vendido_en}-${v.cliente_id}`);

// Perfil de cada clienta que compró ANTES de `desde` (inicio de la campaña):
// su última compra de antes, cuántas compras, cuánto gastó, su categoría y
// talla de siempre, y si ya volvió a comprar desde `desde`.
export function perfilesRecompra(clientes, ventas, desde, hoy = new Date()) {
  const inicio = new Date(desde).getTime();
  const porCliente = new Map();
  ventas.forEach((v) => {
    if (!v.cliente_id) return;
    const l = porCliente.get(v.cliente_id) || [];
    l.push(v);
    porCliente.set(v.cliente_id, l);
  });
  const perfiles = [];
  clientes.forEach((c) => {
    const vs = porCliente.get(c.id) || [];
    const antes = vs.filter((v) => new Date(v.vendido_en).getTime() < inicio);
    if (!antes.length) return; // nunca compró antes de la campaña: no es recompra
    const despues = vs.filter((v) => new Date(v.vendido_en).getTime() >= inicio);
    const ultima = antes.reduce((m, v) => (new Date(v.vendido_en) > new Date(m.vendido_en) ? v : m));
    const dias = Math.floor((hoy.getTime() - new Date(ultima.vendido_en).getTime()) / DIA);
    perfiles.push({
      cliente: c,
      ultima: ultima.vendido_en,
      dias,
      grupo: grupoPorDias(dias),
      compras: new Set(antes.map(ticketDe)).size,
      gastado: antes.reduce((s, v) => s + Number(v.total || 0), 0),
      categoria: moda(antes.map((v) => norm(v.categoria))),
      talla: moda(antes.map((v) => norm(v.talla))),
      ultimoProducto: ultima.nombre_producto || '',
      volvio: despues.length ? {
        compras: new Set(despues.map(ticketDe)).size,
        total: despues.reduce((s, v) => s + Number(v.total || 0), 0),
        primera: despues.reduce((m, v) => (new Date(v.vendido_en) < new Date(m) ? v.vendido_en : m), despues[0].vendido_en),
      } : null,
    });
  });
  return perfiles.sort((a, b) => b.gastado - a.gastado);
}

// ¿Hay suficientes piezas disponibles para mandarla al catálogo filtrado?
// talla + categoría si hay al menos 3; si no, solo la categoría; si no, todo.
export function filtroCatalogo(perfil, productos, prefijoDe, minimo = 3) {
  const conStock = productos.filter((p) => Number(p.stock || 0) > 0);
  const cat = perfil.categoria;
  const enCat = cat ? conStock.filter((p) => norm(p.categoria) === cat) : [];
  if (cat && perfil.talla) {
    const enTalla = enCat.filter((p) => norm(p.talla).toLowerCase() === perfil.talla.toLowerCase());
    if (enTalla.length >= minimo && prefijoDe(cat)) return { cat: prefijoDe(cat), talla: perfil.talla, disponibles: enTalla.length };
  }
  if (enCat.length >= minimo && prefijoDe(cat)) return { cat: prefijoDe(cat), talla: null, disponibles: enCat.length };
  return { cat: null, talla: null, disponibles: conStock.length };
}

// "BRASIER · 36D · NEGRO" → "su brasier"
export function loQueCompro(nombre) {
  const base = norm(nombre).split('·')[0].trim().toLowerCase();
  return base ? `su ${base}` : 'su compra';
}

export function fechaLarga(fechaYMD) {
  const [a, m, d] = String(fechaYMD).split('-').map(Number);
  if (!a) return '';
  return new Date(a, m - 1, d).toLocaleDateString('es-HN', { day: 'numeric', month: 'long' });
}

export function armarMensaje(plantilla, { nombre, tienda, loCompro, talla, descuento, hasta, enlace }) {
  const primerNombre = norm(nombre).split(/\s+/)[0] || '';
  const bonito = primerNombre ? primerNombre[0].toUpperCase() + primerNombre.slice(1).toLowerCase() : '';
  return String(plantilla || '')
    .replaceAll('{nombre}', bonito)
    .replaceAll('{tienda}', tienda || 'la tienda')
    .replaceAll('{lo_que_compro}', loCompro || 'su compra')
    .replaceAll('{talla_txt}', talla ? ` en su talla ${talla}` : '')
    .replaceAll('{descuento}', String(Number(descuento) || 0))
    .replaceAll('{hasta}', hasta || '')
    .replaceAll('{enlace}', enlace || '');
}

// Primer y último día del mes de `hoy`, en 'YYYY-MM-DD' (hora local).
export function mesActual(hoy = new Date()) {
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { desde: ymd(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta: ymd(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)) };
}

// Inicio local de un 'YYYY-MM-DD' (para comparar con vendido_en).
export function inicioDia(ymd) {
  const [a, m, d] = String(ymd).split('-').map(Number);
  return new Date(a, m - 1, d);
}
